'use client';

import {
  memo,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent
} from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowDownUp,
  ChevronDown,
  ChevronUp,
  Download,
  Expand,
  FileText,
  ImageDown,
  Loader2,
  Maximize,
  Maximize2,
  Minus,
  Plus,
  Printer,
  RotateCcw,
  RotateCw,
  Shrink,
  Trash2,
  Search,
  X
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { cn, hexToRgba } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger
} from '@/components/ui/context-menu';
import { ColorPickerPanel } from '@/components/color-picker-popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { PrintPortal } from '@/components/print-portal';
import {
  resolveAuthenticatedBlobUrl,
  useAuthenticatedBlobUrl
} from '@/hooks/use-authenticated-blob-url';
import { exceedsDragThreshold } from '../utils/highlight-draft';
import {
  fullscreenModeAfterChange,
  toggleViewerFullscreen,
  type FullscreenMode
} from '../utils/viewer-fullscreen';

// Local chunk type — kept identical to the OCR feature's shape so this viewer
// can later be reused for OCR'd batches without cross-feature imports.
interface DocumentChunk {
  id: string | number;
  chunk_id: string;
  bbox: { left: number; top: number; width: number; height: number };
  content?: string | null;
  /** Custom-field highlight color (hex), when the owning field has one set. */
  color?: string | null;
}

// ── Zoom levels ────────────────────────────────────────────────────────────
const ZOOM_LEVELS = [0.25, 0.33, 0.5, 0.67, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];
const DEFAULT_ZOOM_INDEX = 5; // 100% — only reached when user manually steps from a fit mode
const MIN_ZOOM = ZOOM_LEVELS[0];
const MAX_ZOOM = ZOOM_LEVELS[ZOOM_LEVELS.length - 1];

// Fallback colors for the "download with highlights" export, cycled by
// chunk order — every extracted field gets a distinct box in the export even
// when its custom color was never set (unlike the live viewer, where an
// uncolored chunk only gets a faint neutral outline).
const DEFAULT_EXPORT_PALETTE = [
  '#8b5cf6',
  '#10b981',
  '#f59e0b',
  '#ec4899',
  '#06b6d4',
  '#6366f1',
  '#84cc16',
  '#d946ef',
  '#14b8a6',
  '#64748b'
];

type Rect = { x: number; y: number; w: number; h: number };

/**
 * A chunk's highlight rect in the *pre-rotation*, image-centered coordinate
 * system — the same numbers `drawPageWithHighlights` feeds to
 * ctx.fillRect/strokeRect while the canvas is translated+rotated.
 */
function chunkHighlightRect(
  bbox: DocumentChunk['bbox'],
  naturalWidth: number,
  naturalHeight: number
): Rect {
  return {
    x: bbox.left * naturalWidth - naturalWidth / 2,
    y: bbox.top * naturalHeight - naturalHeight / 2,
    w: bbox.width * naturalWidth,
    h: bbox.height * naturalHeight
  };
}

/**
 * Rasterizes one page's image plus every chunk's highlight box (custom color,
 * or a palette fallback) onto a canvas, at the page's on-screen rotation.
 */
async function drawPageWithHighlights(
  imageUrl: string,
  chunks: DocumentChunk[],
  rotation: number
): Promise<{
  canvas: HTMLCanvasElement;
  naturalWidth: number;
  naturalHeight: number;
}> {
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('image load failed'));
    img.src = imageUrl;
  });

  const sideways = rotation % 180 !== 0;
  const canvas = document.createElement('canvas');
  canvas.width = sideways ? img.naturalHeight : img.naturalWidth;
  canvas.height = sideways ? img.naturalWidth : img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');

  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

  chunks.forEach((chunk, index) => {
    const color =
      chunk.color ??
      DEFAULT_EXPORT_PALETTE[index % DEFAULT_EXPORT_PALETTE.length];
    const { x, y, w, h } = chunkHighlightRect(
      chunk.bbox,
      img.naturalWidth,
      img.naturalHeight
    );
    ctx.fillStyle = hexToRgba(color, 0.25);
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = hexToRgba(color, 0.9);
    ctx.lineWidth = Math.max(2, img.naturalWidth * 0.002);
    ctx.strokeRect(x, y, w, h);
  });
  ctx.restore();

  return {
    canvas,
    naturalWidth: img.naturalWidth,
    naturalHeight: img.naturalHeight
  };
}

/**
 * Resolves a page's image bytes into a loadable <img> src — the already-
 * resolved blob URL when one was cached from an earlier render, otherwise
 * fetched fresh via the same authenticated-blob cache `PageImage` uses (a
 * page the user never scrolled to yet won't have resolved one).
 */
async function resolvePageImageUrl(
  page: ViewerPage,
  cached: string | undefined
): Promise<string | null> {
  if (cached) return cached;
  if (page.authEndpoint) return resolveAuthenticatedBlobUrl(page.authEndpoint);
  return page.imageUrl;
}

/**
 * Exports every page of the document into one multi-page PDF, each page
 * carrying its own chunks' highlight boxes (custom color, or a palette
 * fallback for uncolored fields) — a full highlighted copy, not just the
 * page currently in view.
 */
async function exportDocumentWithHighlights(
  pages: ViewerPage[],
  resolvedUrls: Map<number, string>,
  rotations: Map<number, number>,
  fileName: string
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  let pdf: InstanceType<typeof jsPDF> | null = null;

  for (let index = 0; index < pages.length; index++) {
    const page = pages[index];
    const url = await resolvePageImageUrl(page, resolvedUrls.get(index));
    if (!url) continue;

    const rotation = rotations.get(index) ?? 0;
    const { canvas } = await drawPageWithHighlights(url, page.chunks, rotation);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const orientation = canvas.width > canvas.height ? 'landscape' : 'portrait';

    if (!pdf) {
      pdf = new jsPDF({
        orientation,
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
    } else {
      pdf.addPage([canvas.width, canvas.height], orientation);
    }
    pdf.addImage(dataUrl, 'JPEG', 0, 0, canvas.width, canvas.height);
  }

  if (!pdf) throw new Error('no page could be rendered');
  pdf.save(fileName);
}

type FitMode = 'none' | 'width' | 'page';

// Padding to leave above a page when scrolling it into view — matches the
// parent flex column's `p-4` so the destination page lands with a comfortable
// top margin instead of being flush against the toolbar.
const SCROLL_TOP_PADDING = 16;
// Base duration (ms) for a one-page glide. The actual duration scales with
// the scroll distance up to ~SCROLL_MAX_DURATION so jumping several pages
// doesn't feel rushed.
const SCROLL_BASE_DURATION = 480;
const SCROLL_MAX_DURATION = 760;
// ease-out-quart — soft start, gentle decel, no overshoot. Matches the curve
// used on the chunk-overlay color transitions for a cohesive feel.
function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}

/**
 * rAF-driven smooth scroll. Unlike `scrollIntoView({behavior:'smooth'})`,
 * this gives us a fixed duration, a custom easing curve, and a precise
 * "done" callback so we can commit state at the exact landing frame.
 */
function smoothScrollTo(
  container: HTMLElement,
  targetTop: number,
  duration: number,
  onDone?: () => void
): () => void {
  const startTop = container.scrollTop;
  const distance = targetTop - startTop;
  if (Math.abs(distance) < 1 || duration <= 0) {
    container.scrollTop = targetTop;
    onDone?.();
    return () => undefined;
  }
  const startTime = performance.now();
  let rafId = 0;
  let cancelled = false;
  const step = (now: number) => {
    if (cancelled) return;
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / duration);
    container.scrollTop = startTop + distance * easeOutQuart(t);
    if (t < 1) {
      rafId = requestAnimationFrame(step);
    } else {
      onDone?.();
    }
  };
  rafId = requestAnimationFrame(step);
  return () => {
    cancelled = true;
    cancelAnimationFrame(rafId);
  };
}

// ── Chunk overlay (bounding box), draggable/resizable when active + editable ─

type Bbox = { left: number; top: number; width: number; height: number };

const MIN_BBOX_SIZE = 0.01;
const RESIZE_HANDLE_SIZE = 8; // px

type ResizeHandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

// Which edges of the un-rotated bbox a given handle controls. This mapping
// never changes with rotation — only the screen-delta → local-delta
// conversion below rotates. A local-NW handle always adjusts the left+top
// edges, even when a 180° rotation puts it visually at the bottom-right.
const RESIZE_HANDLE_EDGES: Record<
  ResizeHandleId,
  { left?: boolean; right?: boolean; top?: boolean; bottom?: boolean }
> = {
  nw: { left: true, top: true },
  n: { top: true },
  ne: { right: true, top: true },
  e: { right: true },
  se: { right: true, bottom: true },
  s: { bottom: true },
  sw: { left: true, bottom: true },
  w: { left: true }
};

const RESIZE_HANDLE_CURSOR: Record<ResizeHandleId, string> = {
  nw: 'nwse-resize',
  n: 'ns-resize',
  ne: 'nesw-resize',
  e: 'ew-resize',
  se: 'nwse-resize',
  s: 'ns-resize',
  sw: 'nesw-resize',
  w: 'ew-resize'
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Converts a screen-space pointer delta into the image's un-rotated local
 * coordinate space. The overlay's percentage-based left/top/width/height
 * always describe the UN-rotated image, while the pointer moves in rotated
 * screen space — so a drag has to be un-rotated before it can be applied to
 * the bbox fractions.
 */
function rotateDelta(
  dxScreen: number,
  dyScreen: number,
  rotation: number
): { dLocalX: number; dLocalY: number } {
  switch (((rotation % 360) + 360) % 360) {
    case 90:
      return { dLocalX: dyScreen, dLocalY: -dxScreen };
    case 180:
      return { dLocalX: -dxScreen, dLocalY: -dyScreen };
    case 270:
      return { dLocalX: -dyScreen, dLocalY: dxScreen };
    default:
      return { dLocalX: dxScreen, dLocalY: dyScreen };
  }
}

/**
 * Style for one resize handle, positioned at a fixed LOCAL compass point
 * relative to the CURRENT bbox's own edges (percentage + a small pixel
 * nudge so it straddles the edge/corner). Reuses `RESIZE_HANDLE_EDGES` so
 * the same edge set drives both placement here and the resize math below —
 * a handle's screen position and what it controls never diverge.
 */
function resizeHandleStyle(handle: ResizeHandleId, bbox: Bbox): CSSProperties {
  const edges = RESIZE_HANDLE_EDGES[handle];
  const half = RESIZE_HANDLE_SIZE / 2;
  const horizontalPct = edges.left
    ? 0
    : edges.right
      ? bbox.width * 100
      : (bbox.width * 100) / 2;
  const verticalPct = edges.top
    ? 0
    : edges.bottom
      ? bbox.height * 100
      : (bbox.height * 100) / 2;

  return {
    left: `calc(${bbox.left * 100 + horizontalPct}% - ${half}px)`,
    top: `calc(${bbox.top * 100 + verticalPct}% - ${half}px)`,
    width: RESIZE_HANDLE_SIZE,
    height: RESIZE_HANDLE_SIZE,
    cursor: RESIZE_HANDLE_CURSOR[handle]
  };
}

const RESIZE_HANDLE_IDS = Object.keys(RESIZE_HANDLE_EDGES) as ResizeHandleId[];

interface DragSession {
  mode: 'move' | 'resize';
  handle?: ResizeHandleId;
  startClientX: number;
  startClientY: number;
  startBbox: Bbox;
  /** Flips once the pointer clears `DRAG_THRESHOLD_PX` — see `handlePointerMove`. */
  engaged: boolean;
}

const ChunkOverlay = memo(function ChunkOverlay({
  chunk,
  isActive,
  isHighlighted,
  isSearchActive,
  isSearchMatch,
  innerWidth,
  innerHeight,
  rotation,
  onChunkClick,
  onChunkHover,
  onBboxChange,
  pendingBbox,
  onColorChange,
  onRemove,
  hasColorOverride = false
}: {
  chunk: DocumentChunk;
  isActive: boolean;
  isHighlighted: boolean;
  isSearchActive: boolean;
  isSearchMatch: boolean;
  innerWidth: number;
  innerHeight: number;
  rotation: number;
  onChunkClick?: (chunkId: string) => void;
  onChunkHover?: (chunkId: string | null) => void;
  /**
   * Emits a DRAFT move/resize — it does not persist (OMA-593). The parent holds
   * the draft and shows a Save button; until it saves, the drafted geometry
   * comes back down as `pendingBbox`.
   */
  onBboxChange?: (chunkId: string, bbox: Bbox) => void;
  /**
   * Unsaved geometry for this chunk. Wins over `chunk.bbox`, so the box stays
   * where the operator dropped it instead of snapping back when the local
   * drag state clears.
   */
  pendingBbox?: Bbox;
  /** Recolors the owning field for this document only; absent = no recoloring. */
  onColorChange?: (chunkId: string, color: string | null) => void;
  /** Unlinks this highlight from its field (OMA-592); absent = not removable. */
  onRemove?: (chunkId: string) => void;
  hasColorOverride?: boolean;
}) {
  const t = useTranslations('ocr');
  const isEmphasized = isActive || isHighlighted;
  const editable = isActive && !!onBboxChange;
  const [liveBbox, setLiveBbox] = useState<Bbox | null>(null);
  const committedBbox = pendingBbox ?? chunk.bbox;
  const dragRef = useRef<DragSession | null>(null);
  // A move-drag ends with a native `click` firing on the same element (the
  // pointerup lands on it, captured or not) — without this, that composed
  // click re-fires `onChunkClick` on every drag, re-selecting the field and
  // triggering its scroll-into-view/expand side effects for no reason.
  const suppressClickRef = useRef(false);
  const bbox = liveBbox ?? committedBbox;

  // A field-level custom color overrides the red/blue/default tint — but
  // never the orange search states, which must always look the same
  // regardless of any field color.
  const colorStyle =
    !isSearchActive && !isSearchMatch && chunk.color
      ? {
          backgroundColor: hexToRgba(chunk.color, isEmphasized ? 0.3 : 0.06),
          borderWidth: '1.5px',
          borderColor: hexToRgba(chunk.color, isEmphasized ? 0.9 : 0.25),
          boxShadow: isEmphasized
            ? `0 0 0 3px ${hexToRgba(chunk.color, 0.4)}`
            : undefined
        }
      : undefined;

  const startDrag = (
    e: ReactPointerEvent<HTMLDivElement>,
    mode: 'move' | 'resize',
    handle?: ResizeHandleId
  ) => {
    // Suppresses the emulated mousedown/mousemove/mouseup the browser fires
    // alongside pointer events — without this, the scroll container's
    // grab-to-pan handlers (plain onMouseDown/onMouseMove) see those and pan
    // the whole viewer at the same time as the box is dragged/resized.
    // Secondary buttons open the recolor/remove context menu instead. Arming a
    // drag on them captured the pointer, so moving the mouse into that menu
    // dragged the box along and left an unsaved "move" behind the recolor.
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      mode,
      handle,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startBbox: bbox,
      engaged: false
    };
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const session = dragRef.current;
    if (!session) return;
    const dxScreen = e.clientX - session.startClientX;
    const dyScreen = e.clientY - session.startClientY;
    // A click on a box selects its field, and the pointer always slips a pixel
    // or two mid-click; below the threshold that slip is not an edit. Latched,
    // so a real drag that returns near its origin still commits.
    if (!session.engaged) {
      if (!exceedsDragThreshold(dxScreen, dyScreen)) return;
      session.engaged = true;
    }
    const { dLocalX, dLocalY } = rotateDelta(dxScreen, dyScreen, rotation);
    const dLeft = dLocalX / innerWidth;
    const dTop = dLocalY / innerHeight;
    const { startBbox } = session;

    if (session.mode === 'move') {
      const newLeft = clamp(startBbox.left + dLeft, 0, 1 - startBbox.width);
      const newTop = clamp(startBbox.top + dTop, 0, 1 - startBbox.height);
      setLiveBbox({ ...startBbox, left: newLeft, top: newTop });
      return;
    }

    const edges = RESIZE_HANDLE_EDGES[session.handle!];
    let { left, top, width, height } = startBbox;
    if (edges.left) {
      const newLeft = clamp(
        startBbox.left + dLeft,
        0,
        startBbox.left + startBbox.width - MIN_BBOX_SIZE
      );
      width = startBbox.left + startBbox.width - newLeft;
      left = newLeft;
    }
    if (edges.right) {
      width = clamp(startBbox.width + dLeft, MIN_BBOX_SIZE, 1 - startBbox.left);
    }
    if (edges.top) {
      const newTop = clamp(
        startBbox.top + dTop,
        0,
        startBbox.top + startBbox.height - MIN_BBOX_SIZE
      );
      height = startBbox.top + startBbox.height - newTop;
      top = newTop;
    }
    if (edges.bottom) {
      height = clamp(startBbox.height + dTop, MIN_BBOX_SIZE, 1 - startBbox.top);
    }
    setLiveBbox({ left, top, width, height });
  };

  const handlePointerUp = () => {
    const session = dragRef.current;
    dragRef.current = null;
    if (!session) return;
    const moved =
      liveBbox != null &&
      (liveBbox.left !== committedBbox.left ||
        liveBbox.top !== committedBbox.top ||
        liveBbox.width !== committedBbox.width ||
        liveBbox.height !== committedBbox.height);
    if (moved) {
      onBboxChange?.(chunk.chunk_id, liveBbox);
      if (session.mode === 'move') suppressClickRef.current = true;
    }
    // Clear immediately — the parent records the draft synchronously and hands
    // it straight back as `pendingBbox`, so there's no visual jump.
    setLiveBbox(null);
  };

  const handleBoxClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onChunkClick?.(chunk.chunk_id);
  };

  const box = (
    <div
      className={cn(
        'absolute cursor-pointer rounded-[2px]',
        // Targeted transitions only — bbox position is set by inline
        // style and doesn't change once mounted, so we animate only the
        // visible state shift.
        'transition-[background-color,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
        // Active / search-active states drop the inner border and most of
        // the fill so the text underneath stays readable — emphasis comes
        // from the OUTER glow ring (drawn outside the bbox via
        // box-shadow), not from overlaying color on the text itself.
        // Multi-region values (boxes_2d) stack three of these at once;
        // tinting heavily would otherwise black out the underlying glyphs.
        isSearchActive
          ? 'bg-orange-500/30 shadow-[0_0_0_3px_rgba(249,115,22,0.45)]'
          : isSearchMatch
            ? 'border-[1.5px] border-orange-400/60 bg-orange-400/10'
            : colorStyle
              ? undefined
              : isActive
                ? 'bg-red-500/30'
                : isHighlighted
                  ? 'bg-blue-500/30 shadow-[0_0_0_3px_rgba(59,130,246,0.40)]'
                  : 'border-primary/20 bg-primary/5 hover:border-primary/40 hover:bg-primary/10 border-[1.5px]'
      )}
      style={{
        left: `${bbox.left * 100}%`,
        top: `${bbox.top * 100}%`,
        width: `${bbox.width * 100}%`,
        height: `${bbox.height * 100}%`,
        ...colorStyle
      }}
      onClick={handleBoxClick}
      onMouseEnter={() => onChunkHover?.(chunk.chunk_id)}
      onMouseLeave={() => onChunkHover?.(null)}
      // Excluded from the scroll container's grab-to-pan detection below
      // (see `handlePanMouseDown`'s `data-no-pan` check) — without this, a
      // move-drag on the box also pans the whole viewer underneath it.
      data-no-pan={editable || undefined}
      onPointerDown={editable ? (e) => startDrag(e, 'move') : undefined}
      onPointerMove={editable ? handlePointerMove : undefined}
      onPointerUp={editable ? handlePointerUp : undefined}
      // Only on the box the operator is actually working with — a native
      // tooltip on every box would fire all over a dense page.
      title={
        onColorChange && isEmphasized ? t('rightClickToRecolor') : undefined
      }
    />
  );

  return (
    <>
      {/* Recoloring and removal live on the box's right-click menu. A visible
          button would have to sit at a corner, where the bbox resize handles
          already are — the context menu keeps the gestures conflict-free. */}
      {onColorChange || onRemove ? (
        <ContextMenu>
          <ContextMenuTrigger asChild>{box}</ContextMenuTrigger>
          <ContextMenuContent
            className='w-auto p-3'
            // Radix menus run keyboard typeahead over their content; without
            // this the hex field's keystrokes get swallowed as menu navigation.
            onKeyDown={(e) => e.stopPropagation()}
          >
            {onColorChange && (
              <ColorPickerPanel
                value={chunk.color ?? null}
                onChange={(hex) => onColorChange(chunk.chunk_id, hex)}
                onReset={
                  hasColorOverride
                    ? () => onColorChange(chunk.chunk_id, null)
                    : undefined
                }
                resetLabel={t('resetFieldHighlightColor')}
              />
            )}
            {onRemove && (
              <>
                {onColorChange && <ContextMenuSeparator className='my-2' />}
                <ContextMenuItem
                  className='text-destructive focus:text-destructive gap-2'
                  onSelect={() => onRemove(chunk.chunk_id)}
                >
                  <Trash2 className='h-3.5 w-3.5' />
                  {t('removeHighlight')}
                </ContextMenuItem>
              </>
            )}
          </ContextMenuContent>
        </ContextMenu>
      ) : (
        box
      )}

      {editable &&
        RESIZE_HANDLE_IDS.map((handle) => (
          <div
            key={handle}
            className='bg-background border-primary absolute z-30 rounded-full border-2'
            style={resizeHandleStyle(handle, bbox)}
            data-no-pan
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => {
              e.stopPropagation();
              startDrag(e, 'resize', handle);
            }}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />
        ))}
    </>
  );
});

/**
 * Drag-to-draw layer for a field that has no highlight yet (OMA-592).
 *
 * Mounted over one page only while the operator is drawing, and only on the
 * page they are drawing on, so a stray drag elsewhere in the document cannot
 * create a region on the wrong page. Emits nothing until the pointer is
 * released, and drops a box smaller than `MIN_BBOX_SIZE` — a click that
 * happens to land on the page is not a highlight.
 */
function DrawHighlightLayer({
  innerWidth,
  innerHeight,
  rotation,
  draft,
  onDraw
}: {
  innerWidth: number;
  innerHeight: number;
  rotation: number;
  /** The box already drawn but not yet saved; keeps it visible while the operator decides. */
  draft: Bbox | null;
  onDraw: (bbox: Bbox) => void;
}) {
  const [live, setLive] = useState<Bbox | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const box = live ?? draft;

  /**
   * Pointer offset within the layer, un-rotated into the image's own
   * coordinate space — the same conversion `rotateDelta` does for a drag,
   * applied to an absolute point instead of a delta.
   */
  const pointIn = (e: ReactPointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    switch (((rotation % 360) + 360) % 360) {
      case 90:
        return { x: offsetY / innerWidth, y: 1 - offsetX / innerHeight };
      case 180:
        return { x: 1 - offsetX / innerWidth, y: 1 - offsetY / innerHeight };
      case 270:
        return { x: 1 - offsetY / innerWidth, y: offsetX / innerHeight };
      default:
        return { x: offsetX / innerWidth, y: offsetY / innerHeight };
    }
  };

  const rectBetween = (
    a: { x: number; y: number },
    b: { x: number; y: number }
  ): Bbox => {
    const left = clamp(Math.min(a.x, b.x), 0, 1);
    const top = clamp(Math.min(a.y, b.y), 0, 1);
    return {
      left,
      top,
      width: clamp(Math.abs(b.x - a.x), 0, 1 - left),
      height: clamp(Math.abs(b.y - a.y), 0, 1 - top)
    };
  };

  return (
    <div
      className='absolute inset-0 z-40 cursor-crosshair'
      data-no-pan
      onPointerDown={(e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        startRef.current = pointIn(e);
        setLive(null);
      }}
      onPointerMove={(e) => {
        const start = startRef.current;
        if (!start) return;
        setLive(rectBetween(start, pointIn(e)));
      }}
      onPointerUp={(e) => {
        const start = startRef.current;
        startRef.current = null;
        if (!start) return;
        const next = rectBetween(start, pointIn(e));
        setLive(null);
        // A click, not a drag — ignore rather than create a dot-sized region
        // the operator would then have to find and delete.
        if (next.width < MIN_BBOX_SIZE || next.height < MIN_BBOX_SIZE) return;
        onDraw(next);
      }}
    >
      {box && (
        <div
          className='border-primary bg-primary/20 pointer-events-none absolute border-2 border-dashed'
          style={{
            left: `${box.left * 100}%`,
            top: `${box.top * 100}%`,
            width: `${box.width * 100}%`,
            height: `${box.height * 100}%`
          }}
        />
      )}
    </div>
  );
}

/**
 * A highlight the operator drew that has no chunk row yet (OMA-592).
 *
 * Dashed and un-draggable on purpose: until Save mints its `chunk_id` there is
 * nothing for a move to PATCH, so the honest affordance is "remove and redraw"
 * rather than a handle that silently discards the drag.
 */
function UnsavedHighlightBox({
  bbox,
  onRemove
}: {
  bbox: Bbox;
  onRemove?: () => void;
}) {
  const t = useTranslations('ocr');

  const box = (
    <div
      className='border-primary bg-primary/20 absolute z-20 rounded-[2px] border-2 border-dashed'
      data-no-pan
      style={{
        left: `${bbox.left * 100}%`,
        top: `${bbox.top * 100}%`,
        width: `${bbox.width * 100}%`,
        height: `${bbox.height * 100}%`
      }}
      title={t('unsavedHighlight')}
    />
  );

  if (!onRemove) return box;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{box}</ContextMenuTrigger>
      <ContextMenuContent className='w-auto'>
        <ContextMenuItem
          className='text-destructive focus:text-destructive gap-2'
          onSelect={onRemove}
        >
          <Trash2 className='h-3.5 w-3.5' />
          {t('removeHighlight')}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

// ── Single page image with chunk overlays ──────────────────────────────────
function PageImage({
  imageUrl,
  authEndpoint,
  chunks,
  highlightedChunkIds,
  activeChunkIds,
  searchMatchChunkIds,
  activeSearchChunkId,
  isProcessing,
  onChunkClick,
  onChunkHover,
  onChunkBboxChange,
  pendingBboxes,
  hiddenChunkIds,
  unsavedHighlights,
  drawDraft,
  onDrawHighlight,
  onRemoveHighlight,
  onChunkColorChange,
  overriddenColorChunkIds,
  onImageLoad,
  rotation,
  innerWidth,
  innerHeight,
  outerWidth,
  outerHeight
}: {
  imageUrl: string | null;
  authEndpoint?: string | null;
  chunks: DocumentChunk[];
  highlightedChunkIds: string[];
  activeChunkIds: string[];
  searchMatchChunkIds: string[];
  activeSearchChunkId: string | null;
  isProcessing: boolean;
  onChunkClick?: (chunkId: string) => void;
  onChunkHover?: (chunkId: string | null) => void;
  /** Emits a DRAFT drag-resize/move of a chunk's bounding box (not persisted). */
  onChunkBboxChange?: (chunkId: string, bbox: Bbox) => void;
  /** Unsaved geometry per chunk id, drawn in place of the stored bbox. */
  pendingBboxes?: ReadonlyMap<string, Bbox>;
  /** Chunks queued for removal — hidden so Cancel/undo can bring them back. */
  hiddenChunkIds?: ReadonlySet<string>;
  /** Boxes drawn on THIS page that have no chunk row yet. */
  unsavedHighlights?: ReadonlyArray<{ tempId: string; bbox: Bbox }>;
  /** Non-null puts THIS page in draw mode; the value is the box drawn so far. */
  drawDraft?: { active: boolean; bbox: Bbox | null } | null;
  onDrawHighlight?: (bbox: Bbox) => void;
  /** Unlinks a highlight from its field; absent = highlights are not removable. */
  onRemoveHighlight?: (chunkId: string) => void;
  /** Recolors a chunk's owning field for this document only (right-click menu). */
  onChunkColorChange?: (chunkId: string, color: string | null) => void;
  /** Chunk ids whose color currently comes from a per-document override. */
  overriddenColorChunkIds?: ReadonlySet<string>;
  onImageLoad?: (
    naturalWidth: number,
    naturalHeight: number,
    resolvedUrl: string
  ) => void;
  rotation: number;
  /** Un-rotated image size (natural × zoom). */
  innerWidth: number;
  innerHeight: number;
  /** Rotation-aware outer slot — equals inner when rotation is 0°/180°,
   *  swapped when 90°/270°. The wrapper reserves this space so a rotated
   *  page doesn't bleed into its neighbours. */
  outerWidth: number;
  outerHeight: number;
}) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);
  // When `authEndpoint` is set, the page bytes are served by a permissioned
  // API route that needs a Bearer token — fetch as a blob via the shared LRU
  // cache so the URL can be embedded as a regular <img src>.
  const blobUrl = useAuthenticatedBlobUrl(authEndpoint ?? null);
  const resolvedUrl = authEndpoint ? blobUrl : imageUrl;
  // Render-phase reset: when the URL changes (e.g. after rotation) we want the
  // spinner back until the new image fires `onLoad`. Doing this in `useEffect`
  // would trigger a cascading render — React's docs recommend this pattern.
  const [trackedUrl, setTrackedUrl] = useState<string | null>(resolvedUrl);
  if (trackedUrl !== resolvedUrl) {
    setTrackedUrl(resolvedUrl);
    setLoaded(false);
  }

  if (!resolvedUrl) {
    return (
      <div
        className='bg-muted/20 mx-auto flex items-center justify-center rounded-lg'
        style={{ width: outerWidth, height: outerHeight }}
      >
        <FileText className='text-muted-foreground/20 h-12 w-12' />
      </div>
    );
  }

  return (
    <div
      className='relative mx-auto select-none'
      style={{ width: outerWidth, height: outerHeight }}
    >
      {/* Inner container is sized for the un-rotated image and rotates around
          its own center. The outer wrapper reserves the rotation-aware
          bounding box so neighbouring pages aren't pushed around. */}
      <div
        className='absolute top-1/2 left-1/2 transition-transform duration-200'
        style={{
          width: innerWidth,
          height: innerHeight,
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- authenticated document scan; next/image proxy cannot forward Bearer token, and OCR review requires full-resolution */}
        <img
          ref={imgRef}
          src={resolvedUrl}
          alt='Page'
          loading='lazy'
          decoding='async'
          className={cn(
            'block h-full w-full rounded shadow-md',
            !loaded && 'bg-muted/20'
          )}
          onLoad={(e) => {
            setLoaded(true);
            onImageLoad?.(
              e.currentTarget.naturalWidth,
              e.currentTarget.naturalHeight,
              resolvedUrl
            );
          }}
          draggable={false}
        />

        {/* Chunk bounding-box overlays — percentage-based so they track the
            un-rotated image, then ride along with the parent's rotation. */}
        <div className='absolute inset-0'>
          {chunks
            .filter((chunk) => !hiddenChunkIds?.has(chunk.chunk_id))
            .map((chunk) => (
              <ChunkOverlay
                key={chunk.id}
                chunk={chunk}
                isActive={activeChunkIds.includes(chunk.chunk_id)}
                isHighlighted={highlightedChunkIds.includes(chunk.chunk_id)}
                isSearchActive={activeSearchChunkId === chunk.chunk_id}
                isSearchMatch={searchMatchChunkIds.includes(chunk.chunk_id)}
                innerWidth={innerWidth}
                innerHeight={innerHeight}
                rotation={rotation}
                onChunkClick={onChunkClick}
                onChunkHover={onChunkHover}
                onBboxChange={onChunkBboxChange}
                pendingBbox={pendingBboxes?.get(chunk.chunk_id)}
                onColorChange={onChunkColorChange}
                onRemove={onRemoveHighlight}
                hasColorOverride={
                  overriddenColorChunkIds?.has(chunk.chunk_id) ?? false
                }
              />
            ))}

          {/* Drawn but not yet saved — dashed so it reads as pending, and
              removable from the same right-click gesture as a real one. */}
          {unsavedHighlights?.map((pending) => (
            <UnsavedHighlightBox
              key={pending.tempId}
              bbox={pending.bbox}
              onRemove={
                onRemoveHighlight
                  ? () => onRemoveHighlight(pending.tempId)
                  : undefined
              }
            />
          ))}

          {drawDraft?.active && onDrawHighlight && (
            <DrawHighlightLayer
              innerWidth={innerWidth}
              innerHeight={innerHeight}
              rotation={rotation}
              draft={drawDraft.bbox}
              onDraw={onDrawHighlight}
            />
          )}

          {/* Scanning animation during processing */}
          <AnimatePresence>
            {isProcessing && (
              <motion.div
                className='pointer-events-none absolute inset-0 overflow-hidden'
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
              >
                <motion.div
                  className='absolute inset-x-0'
                  animate={{ top: ['-2%', '102%'] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'linear',
                    repeatDelay: 0.3
                  }}
                >
                  <div className='via-primary h-px bg-gradient-to-r from-transparent to-transparent' />
                  <div className='via-primary/20 h-4 -translate-y-2 bg-gradient-to-r from-transparent to-transparent blur-lg' />
                </motion.div>

                {(
                  [
                    'start-0 top-0 border-s-2 border-t-2',
                    'end-0 top-0 border-e-2 border-t-2',
                    'start-0 bottom-0 border-s-2 border-b-2',
                    'end-0 bottom-0 border-e-2 border-b-2'
                  ] as const
                ).map((classes, i) => (
                  <motion.div
                    key={i}
                    className={cn(
                      'border-primary/50 absolute h-5 w-5',
                      classes
                    )}
                    animate={{ opacity: [0.3, 0.8, 0.3] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.15,
                      ease: 'easeInOut'
                    }}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {!loaded && (
        <div className='pointer-events-none absolute inset-0 flex items-center justify-center'>
          <Loader2 className='text-muted-foreground h-6 w-6 animate-spin' />
        </div>
      )}
    </div>
  );
}

// ── Types ──────────────────────────────────────────────────────────────────
export interface ViewerPage {
  imageUrl: string | null;
  /**
   * Optional API endpoint that requires a Bearer token. When set, the viewer
   * fetches it as an authenticated blob and uses that instead of `imageUrl`.
   */
  authEndpoint?: string | null;
  label: string;
  chunks: DocumentChunk[];
  isProcessing: boolean;
}

interface SearchMatch {
  pageIndex: number;
  chunkId: string;
}

interface DocumentPagesViewerProps {
  pages: ViewerPage[];
  pdfUrl?: string | null;
  highlightedChunkIds: string[];
  activeChunkIds: string[];
  activePage?: number;
  onChunkClick?: (chunkId: string) => void;
  onChunkHover?: (chunkId: string | null) => void;
  /**
   * Enables manual drag-resize/move of a chunk's bounding box (shown only on
   * the active chunk). Omit to keep the viewer read-only. The callback is a
   * DRAFT emit — the parent decides when it is written (OMA-593).
   */
  onChunkBboxChange?: (chunkId: string, bbox: Bbox) => void;
  /** Unsaved geometry per chunk id, drawn in place of the stored bbox. */
  pendingBboxes?: ReadonlyMap<string, Bbox>;
  /** Chunks queued for removal — hidden so Cancel/undo can bring them back. */
  hiddenChunkIds?: ReadonlySet<string>;
  /** Boxes drawn but not yet saved, keyed to the viewer page they sit on. */
  unsavedHighlights?: ReadonlyArray<{
    tempId: string;
    pageIndex: number;
    bbox: Bbox;
  }>;
  /**
   * Drag-to-draw a highlight for a field (OMA-592). Non-null puts the viewer in
   * draw mode: every page accepts the drag, and the mode stays armed so several
   * regions can be drawn for the same value in one go.
   */
  drawHighlight?: { active: boolean } | null;
  onDrawHighlight?: (pageIndex: number, bbox: Bbox) => void;
  /** Unlinks a highlight from its field; absent = highlights are not removable. */
  onRemoveHighlight?: (chunkIdOrTempId: string) => void;
  /**
   * Recolors a chunk's owning field for THIS document only, from a right-click
   * menu on the highlight box. Omit to make the boxes' colors read-only.
   */
  onChunkColorChange?: (chunkId: string, color: string | null) => void;
  /** Chunk ids currently colored by a per-document override (enables "reset"). */
  overriddenColorChunkIds?: ReadonlySet<string>;
  onPageChange?: (pageIndex: number) => void;
  /**
   * Persist rotation on the server. When provided, the rotate buttons call
   * this with the page index and delta in degrees (90 or -90) instead of
   * applying a client-side CSS transform — the parent should refetch the
   * affected page image after the mutation resolves.
   */
  onRotatePage?: (pageIndex: number, delta: number) => void;
  /** Renders a spinner on the rotate buttons and disables them. */
  isRotating?: boolean;
  /**
   * When provided, the toolbar shows a Rearrange button. The parent owns the
   * dialog and the persistence — the viewer only emits the click.
   */
  onRearrangePages?: () => void;
}

// ── Search bar ────────────────────────────────────────────────────────────
function SearchBar({
  matches,
  currentIndex,
  onQueryChange,
  onNext,
  onPrev,
  onClose
}: {
  matches: SearchMatch[];
  currentIndex: number;
  onQueryChange: (q: string) => void;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
}) {
  const t = useTranslations('ocr');
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (value: string) => {
    setQuery(value);
    onQueryChange(value);
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onNext();
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      onPrev();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.15 }}
      className='absolute inset-x-0 bottom-14 z-10 flex justify-center px-2 sm:px-4'
    >
      <div className='bg-background/95 flex max-w-full items-center gap-1.5 rounded-lg border px-2 py-1.5 shadow-md backdrop-blur-sm'>
        <Search className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('searchPlaceholder')}
          className='h-7 w-32 border-0 bg-transparent px-1 text-xs shadow-none focus-visible:ring-0 sm:w-48'
        />

        {query.trim() && (
          <span className='text-muted-foreground min-w-[3.5rem] text-center text-[10px] tabular-nums'>
            {matches.length > 0
              ? t('matchCount', {
                  current: currentIndex + 1,
                  total: matches.length
                })
              : t('noMatches')}
          </span>
        )}

        <Separator orientation='vertical' className='mx-0.5 h-4' />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-6 w-6'
              onClick={onPrev}
              disabled={matches.length === 0}
            >
              <ChevronUp className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('prevMatch')}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-6 w-6'
              onClick={onNext}
              disabled={matches.length === 0}
            >
              <ChevronDown className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('nextMatch')}</p>
          </TooltipContent>
        </Tooltip>

        <Separator orientation='vertical' className='mx-0.5 h-4' />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-6 w-6'
              onClick={onClose}
            >
              <X className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('closeSearch')}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </motion.div>
  );
}

// ── Toolbar ────────────────────────────────────────────────────────────────
function ViewerToolbar({
  currentPage,
  totalPages,
  effectiveZoom,
  fitMode,
  canDownload,
  canExportHighlights,
  isRotating,
  onPrevPage,
  onNextPage,
  onGoToPage,
  onZoomIn,
  onZoomOut,
  onFitToWidth,
  onFitToPage,
  onRotateLeft,
  onRotateRight,
  onToggleSearch,
  onPrint,
  onDownload,
  onExportHighlights,
  onRearrangePages,
  isFullscreen,
  onToggleFullscreen
}: {
  currentPage: number;
  totalPages: number;
  effectiveZoom: number;
  fitMode: FitMode;
  canDownload: boolean;
  canExportHighlights: boolean;
  isRotating: boolean;
  onPrevPage: () => void;
  onNextPage: () => void;
  onGoToPage: (page: number) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitToWidth: () => void;
  onFitToPage: () => void;
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onToggleSearch: () => void;
  onPrint: () => void;
  onDownload: () => void;
  onExportHighlights: () => void;
  onRearrangePages?: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}) {
  const t = useTranslations('ocr');
  const fullscreenLabel = isFullscreen
    ? t('exitFullscreen')
    : t('enterFullscreen');
  const zoomPercent = `${Math.round(effectiveZoom * 100)}%`;
  const canZoomIn = effectiveZoom < MAX_ZOOM - 0.001;
  const canZoomOut = effectiveZoom > MIN_ZOOM + 0.001;

  const [pageInput, setPageInput] = useState(String(currentPage + 1));

  // Sync input when page changes externally
  useEffect(() => {
    setPageInput(String(currentPage + 1));
  }, [currentPage]);

  const commitPageInput = () => {
    const num = parseInt(pageInput, 10);
    if (!isNaN(num) && num >= 1 && num <= totalPages) {
      onGoToPage(num - 1);
    } else {
      setPageInput(String(currentPage + 1));
    }
  };

  const handlePageInputKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitPageInput();
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className='pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center px-2 pb-3 sm:px-4'>
      <div
        className={cn(
          'bg-background/90 pointer-events-auto flex max-w-full items-center gap-1 overflow-x-auto rounded-lg border px-2 py-1 shadow-md backdrop-blur-sm',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          '[&>*]:shrink-0'
        )}
      >
        {/* ── Zoom controls ── */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onZoomOut}
              disabled={!canZoomOut}
            >
              <Minus className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('zoomOut')}</p>
          </TooltipContent>
        </Tooltip>

        <span
          className={cn(
            'min-w-[3rem] text-center text-xs font-medium tabular-nums',
            fitMode !== 'none' && 'text-primary'
          )}
          title={
            fitMode === 'page'
              ? t('fitToPage')
              : fitMode === 'width'
                ? t('fitToWidth')
                : undefined
          }
        >
          {zoomPercent}
        </span>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onZoomIn}
              disabled={!canZoomIn}
            >
              <Plus className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('zoomIn')}</p>
          </TooltipContent>
        </Tooltip>

        {/* ── Fit to page ── */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={fitMode === 'page' ? 'secondary' : 'ghost'}
              size='icon'
              className='h-7 w-7'
              onClick={onFitToPage}
            >
              <Maximize2 className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('fitToPage')}</p>
          </TooltipContent>
        </Tooltip>

        {/* ── Fit to width ── */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={fitMode === 'width' ? 'secondary' : 'ghost'}
              size='icon'
              className='h-7 w-7'
              onClick={onFitToWidth}
            >
              <Maximize className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('fitToWidth')}</p>
          </TooltipContent>
        </Tooltip>

        <Separator orientation='vertical' className='mx-1 h-5' />

        {/* ── Page navigation ── */}
        {totalPages > 1 && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Input
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onBlur={commitPageInput}
                  onKeyDown={handlePageInputKeyDown}
                  className='h-7 w-10 border-0 bg-transparent px-0 text-center text-xs font-medium tabular-nums shadow-none focus-visible:ring-1'
                  title={t('goToPage')}
                />
              </TooltipTrigger>
              <TooltipContent side='top'>
                <p>{t('goToPage')}</p>
              </TooltipContent>
            </Tooltip>

            <span className='text-muted-foreground text-xs tabular-nums'>
              / {totalPages}
            </span>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-7 w-7'
                  onClick={onPrevPage}
                  disabled={currentPage <= 0}
                >
                  <ChevronUp className='h-4 w-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent side='top'>
                <p>{t('prevPage')}</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-7 w-7'
                  onClick={onNextPage}
                  disabled={currentPage >= totalPages - 1}
                >
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent side='top'>
                <p>{t('nextPage')}</p>
              </TooltipContent>
            </Tooltip>

            <Separator orientation='vertical' className='mx-1 h-5' />
          </>
        )}

        {/* ── Rotate (left + right) ── */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onRotateLeft}
              disabled={isRotating}
            >
              {isRotating ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <RotateCcw className='h-3.5 w-3.5' />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('rotate')}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onRotateRight}
              disabled={isRotating}
            >
              {isRotating ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <RotateCw className='h-3.5 w-3.5' />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('rotate')}</p>
          </TooltipContent>
        </Tooltip>

        {onRearrangePages && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className='h-7 w-7'
                onClick={onRearrangePages}
                disabled={totalPages < 2}
              >
                <ArrowDownUp className='h-3.5 w-3.5' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='top'>
              <p>{t('rearrangePages')}</p>
            </TooltipContent>
          </Tooltip>
        )}

        <Separator orientation='vertical' className='mx-1 h-5' />

        {/* ── Actions ── */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onToggleSearch}
            >
              <Search className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('findInDocument')}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onPrint}
            >
              <Printer className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('print')}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onDownload}
              disabled={!canDownload}
            >
              <Download className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('download')}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onExportHighlights}
              disabled={!canExportHighlights}
            >
              <ImageDown className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('downloadWithHighlights')}</p>
          </TooltipContent>
        </Tooltip>

        <Separator orientation='vertical' className='mx-1 h-5' />

        {/* ── Fullscreen (OMA-672) ── */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={isFullscreen ? 'secondary' : 'ghost'}
              size='icon'
              className='h-7 w-7'
              onClick={onToggleFullscreen}
              aria-label={fullscreenLabel}
              aria-pressed={isFullscreen}
            >
              {isFullscreen ? (
                <Shrink className='h-3.5 w-3.5' />
              ) : (
                <Expand className='h-3.5 w-3.5' />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{fullscreenLabel}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// ── Print view ────────────────────────────────────────────────────────────
function PrintPage({ page, isLast }: { page: ViewerPage; isLast: boolean }) {
  const blobUrl = useAuthenticatedBlobUrl(page.authEndpoint ?? null);
  const src = page.authEndpoint ? blobUrl : page.imageUrl;

  return (
    <div
      style={{
        width: '194mm',
        height: '281mm',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        fontFamily: 'system-ui, sans-serif',
        background: '#fff',
        pageBreakAfter: isLast ? 'auto' : 'always',
        breakAfter: isLast ? 'auto' : 'page',
        pageBreakInside: 'avoid',
        breakInside: 'avoid'
      }}
    >
      <p
        style={{
          fontSize: '10px',
          color: '#666',
          margin: 0,
          flex: '0 0 auto'
        }}
      >
        {page.label}
      </p>
      {src && (
        /* eslint-disable-next-line @next/next/no-img-element -- authenticated document scan; next/image proxy cannot forward Bearer token, and OCR review requires full-resolution */
        <img
          src={src}
          alt={page.label}
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain'
          }}
        />
      )}
    </div>
  );
}

function PrintView({ pages }: { pages: ViewerPage[] }) {
  return (
    <PrintPortal>
      <div>
        {pages.map((page, i) => (
          <PrintPage key={i} page={page} isLast={i === pages.length - 1} />
        ))}
      </div>
    </PrintPortal>
  );
}

// ── Main viewer ────────────────────────────────────────────────────────────
export function DocumentPagesViewer({
  pages,
  pdfUrl,
  highlightedChunkIds,
  activeChunkIds,
  activePage,
  onChunkClick,
  onChunkHover,
  onChunkBboxChange,
  pendingBboxes,
  hiddenChunkIds,
  unsavedHighlights,
  drawHighlight,
  onDrawHighlight,
  onRemoveHighlight,
  onChunkColorChange,
  overriddenColorChunkIds,
  onPageChange,
  onRotatePage,
  isRotating = false,
  onRearrangePages
}: DocumentPagesViewerProps) {
  const t = useTranslations('ocr');
  const scrollRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // ── Fullscreen (OMA-672) ──
  // The ROOT goes fullscreen, not the scroll area, so the toolbar and search
  // bar come along. Where the Fullscreen API is missing or refuses, the root
  // pins itself over the window instead (`fallback`).
  const rootRef = useRef<HTMLDivElement>(null);
  const [fullscreenMode, setFullscreenMode] = useState<FullscreenMode>('off');
  const handleToggleFullscreen = useCallback(async () => {
    const el = rootRef.current;
    if (!el) return;
    setFullscreenMode(await toggleViewerFullscreen(fullscreenMode, el, document));
  }, [fullscreenMode]);

  useEffect(() => {
    const onChange = () => {
      const el = rootRef.current;
      if (!el) return;
      setFullscreenMode((mode) => fullscreenModeAfterChange(mode, el, document));
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // The browser handles Esc in native mode; the fallback has to. Captured on
  // WINDOW so it runs before the hosting Radix dialog's own Esc listener
  // (registered on the document) — Esc leaves fullscreen first, and only a
  // second Esc closes the dialog.
  useEffect(() => {
    if (fullscreenMode !== 'fallback') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      setFullscreenMode('off');
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [fullscreenMode]);

  // Leaving the screen while fullscreen must not strand the document there.
  useEffect(() => {
    const el = rootRef.current;
    return () => {
      if (el && document.fullscreenElement === el) {
        void document.exitFullscreen().catch(() => {});
      }
    };
  }, []);
  const [visiblePage, setVisiblePage] = useState(0);
  const isAutoScrolling = useRef(false);
  // Latest scroll target — read by the auto-scroll effect so rapid hover
  // changes don't short-circuit while a smooth scroll is still in flight.
  const pendingTargetRef = useRef<number | null>(null);
  // Cancels the current rAF scroll. A new scroll cancels the previous one so
  // rapid page changes glide to the latest target without jitter.
  const cancelScrollRef = useRef<(() => void) | null>(null);

  // Toolbar state
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  // Per-page rotation. Always applied optimistically — when the user clicks
  // rotate the CSS transform updates instantly so they get immediate feedback.
  // If `onRotatePage` is wired, the parent persists rotation on the server
  // and the affected image refetches; we then clear that page's optimistic
  // entry on `onBlobReady` so the rotated bytes aren't double-rotated.
  const [pageRotations, setPageRotations] = useState<Map<number, number>>(
    new Map()
  );
  const [fitMode, setFitMode] = useState<FitMode>('page');
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  // Natural pixel dimensions per loaded page. Kept in state (not a ref) so
  // page wrappers re-render with their `displayWidth = naturalWidth * zoom`
  // once an image reports its dimensions.
  const [imageNaturalDims, setImageNaturalDims] = useState<
    Map<number, { width: number; height: number }>
  >(new Map());
  // Resolved <img src> per page (plain URL or authenticated blob: URL) — kept
  // so "download with highlights" can re-load the exact same bytes into a
  // canvas without re-resolving the auth flow itself.
  const [imageResolvedUrls, setImageResolvedUrls] = useState<
    Map<number, string>
  >(new Map());

  const firstPageRotation = pageRotations.get(0) ?? 0;

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  // Print state
  const [printMode, setPrintMode] = useState(false);

  // Grab-to-pan state. Activated only when the zoomed content overflows the
  // scroll container so the cursor doesn't change in fit modes where panning
  // is impossible.
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isGrabbing, setIsGrabbing] = useState(false);
  const dragStateRef = useRef<{
    moved: boolean;
    startX: number;
    startY: number;
    startScrollLeft: number;
    startScrollTop: number;
  } | null>(null);

  // ── Compute effective zoom ──
  const effectiveZoom = useMemo(() => {
    if (fitMode === 'none' || containerSize.width === 0)
      return ZOOM_LEVELS[zoomIndex];

    const firstDims = imageNaturalDims.get(0);
    if (!firstDims) return ZOOM_LEVELS[zoomIndex];

    const isRotatedSideways = firstPageRotation % 180 !== 0;
    const refW = isRotatedSideways ? firstDims.height : firstDims.width;
    const refH = isRotatedSideways ? firstDims.width : firstDims.height;

    // Padding budget matches the scroll wrapper's `p-4` (16px each side) plus
    // the `pb-14` toolbar gutter (~56px) and the page label row above the image.
    const padX = 32;
    const padY = 32 + 56 + 28;

    const scaleW = Math.max(containerSize.width - padX, 0) / refW;
    if (fitMode === 'width') return scaleW;

    const scaleH = Math.max(containerSize.height - padY, 0) / refH;
    return Math.min(scaleW, scaleH);
  }, [fitMode, containerSize, zoomIndex, firstPageRotation, imageNaturalDims]);

  // ── Track container size for fit modes ──
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
      setIsOverflowing(
        el.scrollWidth > el.clientWidth + 1 ||
          el.scrollHeight > el.clientHeight + 1
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Re-check overflow when zoom / rotation / loaded page dimensions change.
  // The ResizeObserver only fires on container resize, but page wrappers also
  // grow on zoom — without this the cursor would stay default until the next
  // window resize.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    setIsOverflowing(
      el.scrollWidth > el.clientWidth + 1 ||
        el.scrollHeight > el.clientHeight + 1
    );
  }, [effectiveZoom, imageNaturalDims, pageRotations]);

  // ── Grab-to-pan ───────────────────────────────────────────────────────────
  // Click-and-drag scrolling when the document overflows. Uses a small movement
  // threshold so a genuine click on a chunk overlay still fires its handler —
  // only sustained motion is treated as a drag.
  const handlePanMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (!isOverflowing || e.button !== 0) return;
      const el = scrollRef.current;
      if (!el) return;
      const target = e.target as HTMLElement;
      // Don't hijack drags that start on actual controls (toolbar, search,
      // links) or on an editable chunk overlay/resize handle (`data-no-pan`)
      // — otherwise a move/resize drag on a highlight box also pans the
      // whole viewer underneath it. Read-only chunk overlays don't carry
      // `data-no-pan`, so their plain clicks still fire after the movement
      // threshold check below.
      if (
        target.closest(
          'button, a, input, textarea, select, [role="button"], [role="textbox"], [data-no-pan]'
        )
      ) {
        return;
      }
      dragStateRef.current = {
        moved: false,
        startX: e.clientX,
        startY: e.clientY,
        startScrollLeft: el.scrollLeft,
        startScrollTop: el.scrollTop
      };
    },
    [isOverflowing]
  );

  const handlePanMouseMove = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      const state = dragStateRef.current;
      if (!state) return;
      const el = scrollRef.current;
      if (!el) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      if (!state.moved && Math.hypot(dx, dy) < 5) return;
      if (!state.moved) {
        state.moved = true;
        setIsGrabbing(true);
      }
      el.scrollLeft = state.startScrollLeft - dx;
      el.scrollTop = state.startScrollTop - dy;
    },
    []
  );

  const endPan = useCallback(() => {
    const state = dragStateRef.current;
    if (state?.moved) {
      // Swallow the click that fires immediately after a drag-mouseup so the
      // chunk under the cursor doesn't get accidentally activated.
      const suppress = (ev: MouseEvent) => {
        ev.stopPropagation();
        ev.preventDefault();
      };
      window.addEventListener('click', suppress, { capture: true, once: true });
    }
    dragStateRef.current = null;
    setIsGrabbing(false);
  }, []);

  // ── Search logic ──
  const searchMatches = useMemo<SearchMatch[]>(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    const matches: SearchMatch[] = [];
    pages.forEach((page, pageIndex) => {
      page.chunks.forEach((chunk) => {
        if (chunk.content?.toLowerCase().includes(q)) {
          matches.push({ pageIndex, chunkId: chunk.chunk_id });
        }
      });
    });
    return matches;
  }, [searchQuery, pages]);

  // Reset current match when matches change
  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchMatches.length]);

  const searchMatchChunkIds = useMemo(
    () => searchMatches.map((m) => m.chunkId),
    [searchMatches]
  );

  const activeSearchChunkId =
    searchMatches.length > 0 ? searchMatches[currentMatchIndex]?.chunkId : null;

  // Navigate to the current search match page
  useEffect(() => {
    if (searchMatches.length === 0) return;
    const match = searchMatches[currentMatchIndex];
    if (!match) return;
    goToPage(match.pageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMatchIndex, searchMatches]);

  const handleSearchNext = useCallback(() => {
    if (searchMatches.length === 0) return;
    setCurrentMatchIndex((i) => (i + 1) % searchMatches.length);
  }, [searchMatches.length]);

  const handleSearchPrev = useCallback(() => {
    if (searchMatches.length === 0) return;
    setCurrentMatchIndex(
      (i) => (i - 1 + searchMatches.length) % searchMatches.length
    );
  }, [searchMatches.length]);

  const handleSearchClose = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setCurrentMatchIndex(0);
  }, []);

  // ── Auto-scroll to activePage when it changes externally ──
  // The rAF-driven `smoothScrollTo` gives precise easing and an exact
  // landing-frame callback. Each new scroll cancels the prior one, so
  // rapid hover changes glide to the latest target without jitter and
  // `visiblePage` commits exactly when the animation lands — no timers.
  useEffect(() => {
    if (activePage == null) return;
    const currentTarget = pendingTargetRef.current ?? visiblePage;
    if (activePage === currentTarget) return;
    const container = scrollRef.current;
    const el = pageRefs.current.get(activePage);
    if (!container || !el) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    cancelScrollRef.current?.();
    isAutoScrolling.current = true;
    pendingTargetRef.current = activePage;
    const target = activePage;
    const targetTop = Math.max(0, el.offsetTop - SCROLL_TOP_PADDING);

    if (prefersReducedMotion) {
      container.scrollTop = targetTop;
      setVisiblePage(target);
      pendingTargetRef.current = null;
      isAutoScrolling.current = false;
      cancelScrollRef.current = null;
      return;
    }

    // Scale the duration with distance — adjacent pages glide quickly, big
    // jumps take a little longer so they feel deliberate, not jarring.
    const distance = Math.abs(targetTop - container.scrollTop);
    const duration = Math.min(
      SCROLL_MAX_DURATION,
      SCROLL_BASE_DURATION + distance * 0.18
    );

    cancelScrollRef.current = smoothScrollTo(
      container,
      targetTop,
      duration,
      () => {
        isAutoScrolling.current = false;
        setVisiblePage(target);
        pendingTargetRef.current = null;
        cancelScrollRef.current = null;
      }
    );
  }, [activePage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cancel any in-flight scroll on unmount.
  useEffect(
    () => () => {
      cancelScrollRef.current?.();
    },
    []
  );

  // ── Keep the same page in view across a zoom / fit change ──
  // Page heights are `naturalHeight * effectiveZoom`, so the whole document
  // rescales the moment the fit resolves — and the untouched `scrollTop` then
  // points at an entirely different page, which `handleScroll` dutifully
  // reports as a page change. On load that is a visible jump: the viewer is
  // told to show page 2, lands there at 100%, the fit drops it to 36%, and the
  // reported page becomes 3. Re-anchoring on the page we were already on makes
  // a zoom change what the user expects — a resize, not a navigation.
  useEffect(() => {
    const container = scrollRef.current;
    const target = pendingTargetRef.current ?? visiblePage;
    const el = pageRefs.current.get(target);
    if (!container || !el) return;

    // Next frame: the page wrappers have to re-layout at the new zoom before
    // their offsets mean anything.
    const id = requestAnimationFrame(() => {
      const node = pageRefs.current.get(target);
      if (!node) return;
      // Direct assignment, not a smooth scroll: this corrects for a relayout
      // that already happened, so an animation would read as drift.
      container.scrollTop = Math.max(0, node.offsetTop - SCROLL_TOP_PADDING);
    });

    return () => cancelAnimationFrame(id);
    // `visiblePage` is deliberately NOT a dependency — this must fire on zoom
    // changes only, or every page turn would re-anchor itself mid-scroll.
  }, [effectiveZoom]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Track visible page from user scroll ──
  const handleScroll = useCallback(() => {
    if (isAutoScrolling.current) return;
    const container = scrollRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    let closestIdx = 0;
    let closestDist = Infinity;

    for (const [idx, el] of pageRefs.current) {
      const dist = Math.abs(el.offsetTop - scrollTop - 16);
      if (dist < closestDist) {
        closestDist = dist;
        closestIdx = idx;
      }
    }

    if (closestIdx !== visiblePage) {
      setVisiblePage(closestIdx);
      onPageChange?.(closestIdx);
    }
  }, [visiblePage, onPageChange]);

  // ── Page navigation ──
  const goToPage = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, pages.length - 1));
      const container = scrollRef.current;
      const el = pageRefs.current.get(clamped);
      if (!container || !el) return;

      // Cancel any in-flight auto-scroll so its stale target doesn't fight
      // this explicit navigation.
      cancelScrollRef.current?.();
      pendingTargetRef.current = null;

      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const targetTop = Math.max(0, el.offsetTop - SCROLL_TOP_PADDING);
      isAutoScrolling.current = true;

      if (prefersReducedMotion) {
        container.scrollTop = targetTop;
        setVisiblePage(clamped);
        onPageChange?.(clamped);
        isAutoScrolling.current = false;
        cancelScrollRef.current = null;
        return;
      }

      const distance = Math.abs(targetTop - container.scrollTop);
      const duration = Math.min(
        SCROLL_MAX_DURATION,
        SCROLL_BASE_DURATION + distance * 0.18
      );

      // Toolbar/UI commits immediately on explicit nav so prev/next/jump
      // feedback is instant; the auto-scroll path waits for the landing
      // frame to avoid mid-glide flicker in the page indicator.
      setVisiblePage(clamped);
      onPageChange?.(clamped);

      cancelScrollRef.current = smoothScrollTo(
        container,
        targetTop,
        duration,
        () => {
          isAutoScrolling.current = false;
          cancelScrollRef.current = null;
        }
      );
    },
    [pages.length, onPageChange]
  );

  // ── Zoom handlers (snap to the nearest standard level, exit fit mode) ──
  const handleZoomIn = useCallback(() => {
    const next = ZOOM_LEVELS.find((z) => z > effectiveZoom + 0.001) ?? MAX_ZOOM;
    setZoomIndex(ZOOM_LEVELS.indexOf(next));
    setFitMode('none');
  }, [effectiveZoom]);

  const handleZoomOut = useCallback(() => {
    const next =
      [...ZOOM_LEVELS].reverse().find((z) => z < effectiveZoom - 0.001) ??
      MIN_ZOOM;
    setZoomIndex(ZOOM_LEVELS.indexOf(next));
    setFitMode('none');
  }, [effectiveZoom]);

  const handleFitToWidth = useCallback(() => {
    setFitMode((m) => (m === 'width' ? 'none' : 'width'));
  }, []);

  const handleFitToPage = useCallback(() => {
    setFitMode((m) => (m === 'page' ? 'none' : 'page'));
  }, []);

  // ── Rotate handler ────────────────────────────────────────────────────────
  // Always apply optimistic rotation client-side for instant feedback. If
  // `onRotatePage` is wired the parent persists it on the server and the
  // affected image will refetch — we clear the optimistic entry once the new
  // blob arrives (see `handleBlobReady`).
  const handleRotate = useCallback(
    (delta: number) => {
      setPageRotations((prev) => {
        const next = new Map(prev);
        const current = next.get(visiblePage) ?? 0;
        next.set(visiblePage, (((current + delta) % 360) + 360) % 360);
        return next;
      });
      onRotatePage?.(visiblePage, delta);
    },
    [onRotatePage, visiblePage]
  );

  // ── Print handler ──
  const handlePrint = useCallback(async () => {
    setPrintMode(true);

    // Wait two frames for React to commit and the portal to mount
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    await new Promise<void>((r) => requestAnimationFrame(() => r()));

    // Wait for every print image to load + decode — otherwise the print
    // dialog snapshots the page before the blob-backed <img> tags render.
    const portal = document.getElementById('__print-portal');
    if (portal) {
      const imgs = Array.from(portal.querySelectorAll('img'));
      await Promise.all(
        imgs.map(async (img) => {
          if (!img.complete) {
            await new Promise<void>((resolve) => {
              img.addEventListener('load', () => resolve(), { once: true });
              img.addEventListener('error', () => resolve(), { once: true });
            });
          }
          try {
            await img.decode();
          } catch {
            // decode() can reject for cancelled/broken images — print anyway
          }
        })
      );
    }

    window.print();
    setPrintMode(false);
  }, []);

  // ── Download handler ──
  const handleDownload = useCallback(() => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = 'document.pdf';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(t('downloadStarted'));
  }, [pdfUrl, t]);

  // ── Download-with-highlights handler ──
  // Rasterizes every page's image plus its chunks' highlight boxes into one
  // multi-page PDF — pages not yet scrolled into view get fetched fresh via
  // the same authenticated-blob cache `PageImage` uses.
  const handleExportHighlights = useCallback(() => {
    toast.promise(
      exportDocumentWithHighlights(
        pages,
        imageResolvedUrls,
        pageRotations,
        'document-highlighted.pdf'
      ),
      {
        loading: t('highlightsExporting'),
        success: t('downloadStarted'),
        error: t('highlightsExportFailed')
      }
    );
  }, [pages, imageResolvedUrls, pageRotations, t]);

  // ── Image natural dimensions callback ──
  // Also clears any optimistic CSS rotation for this page — once the new
  // server-rotated bytes have loaded, the baked-in rotation is enough and we
  // don't want to double-rotate via transform.
  const handleImageLoad = useCallback(
    (
      pageIndex: number,
      naturalWidth: number,
      naturalHeight: number,
      resolvedUrl: string
    ) => {
      setImageNaturalDims((prev) => {
        const existing = prev.get(pageIndex);
        if (
          existing &&
          existing.width === naturalWidth &&
          existing.height === naturalHeight
        ) {
          return prev;
        }
        const next = new Map(prev);
        next.set(pageIndex, { width: naturalWidth, height: naturalHeight });
        return next;
      });
      setImageResolvedUrls((prev) => {
        if (prev.get(pageIndex) === resolvedUrl) return prev;
        const next = new Map(prev);
        next.set(pageIndex, resolvedUrl);
        return next;
      });
      setPageRotations((prev) => {
        if (!prev.has(pageIndex)) return prev;
        const next = new Map(prev);
        next.delete(pageIndex);
        return next;
      });
    },
    []
  );

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;

      if (mod && e.key === 'f') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
      if (mod && e.key === 'p') {
        e.preventDefault();
        handlePrint();
      }
      if (mod && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        handleZoomIn();
      }
      if (mod && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      }
      if (mod && e.key === '\\') {
        e.preventDefault();
        handleFitToWidth();
      }
      if (mod && e.key === '0') {
        e.preventDefault();
        handleFitToPage();
      }
      if (e.key === 'Escape' && searchOpen) {
        handleSearchClose();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    searchOpen,
    handlePrint,
    handleZoomIn,
    handleZoomOut,
    handleFitToWidth,
    handleFitToPage,
    handleSearchClose
  ]);

  // ── PDF-only fallback (no page images) ──
  if (pages.length === 0 && !pdfUrl) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-3'>
        <FileText className='text-muted-foreground/20 h-16 w-16' />
      </div>
    );
  }

  if (pages.length === 0 && pdfUrl) {
    return (
      <iframe src={pdfUrl} className='h-full w-full' title='Document PDF' />
    );
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        'relative h-full',
        // A native fullscreen element is painted over a black backdrop; the
        // fallback has to cover the window itself.
        fullscreenMode === 'native' && 'bg-background',
        fullscreenMode === 'fallback' && 'bg-background fixed inset-0 z-50'
      )}
    >
      {/* Scrollable page area — overflow-auto so pages wider than the viewport
          at high zoom get scrollbars on both axes, just like an embedded PDF.
          Grab-to-pan kicks in once content actually overflows. */}
      <div
        ref={scrollRef}
        className={cn(
          'h-full overflow-auto pb-14',
          isOverflowing && (isGrabbing ? 'cursor-grabbing' : 'cursor-grab')
        )}
        onScroll={handleScroll}
        onMouseDown={handlePanMouseDown}
        onMouseMove={handlePanMouseMove}
        onMouseUp={endPan}
        onMouseLeave={endPan}
      >
        {/* min-w-max + min-w-full keep the inner column at least as wide as
            the parent (so items-center stays centered when not overflowing)
            while letting it grow with the widest page (so zoom doesn't leak
            sideways into adjacent panels — the parent's overflow-auto clips
            instead). w-fit ensures the inner column hugs the widest child
            when it does exceed the parent. */}
        <div className='flex w-fit min-w-full flex-col items-center gap-6 p-4'>
          {pages.map((page, index) => {
            // Each page is sized in real pixels (naturalWidth × zoom). The
            // wrapper reserves the rotation-aware bounding box so a rotated
            // page stays inside its layout slot instead of bleeding into its
            // neighbours. Until the image loads we fall back to the container
            // width and an A4-ish aspect ratio so the layout doesn't jump.
            const naturalDims = imageNaturalDims.get(index);
            const rotation = pageRotations.get(index) ?? 0;
            const isSideways = rotation % 180 !== 0;
            const fallbackWidth = Math.max(containerSize.width - 32, 320);
            const baseWidth = naturalDims?.width ?? fallbackWidth;
            const baseHeight =
              naturalDims?.height ?? Math.round(fallbackWidth * 1.4142);
            const innerW = baseWidth * effectiveZoom;
            const innerH = baseHeight * effectiveZoom;
            const outerW =
              (isSideways ? baseHeight : baseWidth) * effectiveZoom;
            const outerH =
              (isSideways ? baseWidth : baseHeight) * effectiveZoom;
            return (
              <div
                key={index}
                ref={(el) => {
                  if (el) pageRefs.current.set(index, el);
                  else pageRefs.current.delete(index);
                }}
                style={{ width: `${outerW}px` }}
                className='shrink-0 transition-[width] duration-150'
              >
                {/* Page label */}
                <div className='mb-2 flex items-center gap-2'>
                  <Badge variant='outline' className='text-xs font-medium'>
                    {page.label}
                  </Badge>
                  {page.isProcessing && (
                    <Loader2 className='text-primary h-3 w-3 animate-spin' />
                  )}
                </div>

                {/* Page image with overlays */}
                <PageImage
                  imageUrl={page.imageUrl}
                  authEndpoint={page.authEndpoint}
                  chunks={page.chunks}
                  highlightedChunkIds={highlightedChunkIds}
                  activeChunkIds={activeChunkIds}
                  searchMatchChunkIds={searchMatchChunkIds}
                  activeSearchChunkId={activeSearchChunkId}
                  isProcessing={page.isProcessing}
                  onChunkClick={onChunkClick}
                  onChunkHover={onChunkHover}
                  onChunkBboxChange={onChunkBboxChange}
                  pendingBboxes={pendingBboxes}
                  hiddenChunkIds={hiddenChunkIds}
                  unsavedHighlights={unsavedHighlights?.filter(
                    (h) => h.pageIndex === index
                  )}
                  drawDraft={
                    drawHighlight?.active ? { active: true, bbox: null } : null
                  }
                  onDrawHighlight={
                    onDrawHighlight
                      ? (bbox) => onDrawHighlight(index, bbox)
                      : undefined
                  }
                  onRemoveHighlight={onRemoveHighlight}
                  onChunkColorChange={onChunkColorChange}
                  overriddenColorChunkIds={overriddenColorChunkIds}
                  onImageLoad={(w, h, url) => handleImageLoad(index, w, h, url)}
                  rotation={rotation}
                  innerWidth={innerW}
                  innerHeight={innerH}
                  outerWidth={outerW}
                  outerHeight={outerH}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Search bar */}
      <AnimatePresence>
        {searchOpen && (
          <SearchBar
            matches={searchMatches}
            currentIndex={currentMatchIndex}
            onQueryChange={setSearchQuery}
            onNext={handleSearchNext}
            onPrev={handleSearchPrev}
            onClose={handleSearchClose}
          />
        )}
      </AnimatePresence>

      {/* Toolbar */}
      <ViewerToolbar
        currentPage={visiblePage}
        totalPages={pages.length}
        effectiveZoom={effectiveZoom}
        fitMode={fitMode}
        canDownload={!!pdfUrl}
        canExportHighlights={pages.some((p) => p.chunks.length > 0)}
        isRotating={isRotating}
        onPrevPage={() => goToPage(visiblePage - 1)}
        onNextPage={() => goToPage(visiblePage + 1)}
        onGoToPage={goToPage}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onFitToWidth={handleFitToWidth}
        onFitToPage={handleFitToPage}
        onRotateLeft={() => handleRotate(-90)}
        onRotateRight={() => handleRotate(90)}
        onToggleSearch={() => setSearchOpen((v) => !v)}
        onPrint={handlePrint}
        onDownload={handleDownload}
        onExportHighlights={handleExportHighlights}
        onRearrangePages={onRearrangePages}
        isFullscreen={fullscreenMode !== 'off'}
        onToggleFullscreen={handleToggleFullscreen}
      />

      {/* Print portal */}
      {printMode && <PrintView pages={pages} />}
    </div>
  );
}
